import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  BarChart3,
  Briefcase,
  CalendarDays,
  Clock,
  Crown,
  IdCard,
  Loader2,
  Medal,
  RefreshCw,
  Search,
  Shield,
  Skull,
  Swords,
  TrendingDown,
  TrendingUp,
  Trophy,
  UserMinus,
  UserPlus,
  Users
} from 'lucide-react';

const MEMBERS_CSV_PATH = '/api/alliance/current/members';
const RANKINGS_CSV_PATH = '/api/alliance/current/ranking';
const PREVIOUS_MEMBERS_CSV_PATH = '/api/alliance/previous/members';
const PREVIOUS_RANKINGS_CSV_PATH = '/api/alliance/previous/ranking';
const CSV_META_PATH = '/api/alliance/csv-meta';
const HOME_ALLIANCE_TAG = 'MEXi';

const parseCsvLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"' && next === '"' && inQuotes) {
      current += '"';
      i += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      values.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  values.push(current);
  return values;
};

const parseCsv = (text) => {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];

  const headers = parseCsvLine(lines[0]).map((header) => header.trim());
  return lines.slice(1).map((line, index) => {
    const values = parseCsvLine(line);
    const row = { rowId: index + 1 };
    headers.forEach((header, headerIndex) => {
      row[header] = values[headerIndex]?.trim() || '';
    });
    return row;
  });
};

const loadCsvFile = async (path) => {
  const response = await fetch(`${path}?v=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) throw new Error(`CSV ${response.status}`);

  const buffer = await response.arrayBuffer();
  const text = new TextDecoder('utf-8').decode(buffer);
  return parseCsv(text);
};

const loadOptionalCsvFile = async (path) => {
  const response = await fetch(`${path}?v=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) return [];

  const buffer = await response.arrayBuffer();
  const text = new TextDecoder('utf-8').decode(buffer);
  return parseCsv(text);
};

const toNumber = (value) => Number(String(value || '').replace(/[^0-9.-]/g, '')) || 0;

const formatUpdateDate = (value) => {
  if (!value) return 'Sin fecha';
  const normalized = String(value).replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const rowKey = (row, field) => String(row?.[field] || '').trim().toLowerCase();

const compareMemberRows = (members, previousMembers) => {
  if (!previousMembers.length) {
    return members.map((member) => ({ ...member, comparison: null }));
  }

  const previousByUid = new Map(previousMembers.map((member) => [rowKey(member, 'UID'), member]).filter(([key]) => key));

  return members.map((member) => {
    const previous = previousByUid.get(rowKey(member, 'UID'));
    const powerDelta = previous ? toNumber(member.Power) - toNumber(previous.Power) : 0;
    const killsDelta = previous ? toNumber(member['Army Kills']) - toNumber(previous['Army Kills']) : 0;
    const hqDelta = previous ? toNumber(member['HQ Level']) - toNumber(previous['HQ Level']) : 0;
    const careerLevelDelta = previous ? toNumber(member['Career Lv']) - toNumber(previous['Career Lv']) : 0;

    return {
      ...member,
      comparison: {
        previous,
        status: previous ? (powerDelta > 0 ? 'up' : powerDelta < 0 ? 'down' : 'same') : 'new',
        powerDelta,
        killsDelta,
        hqDelta,
        careerLevelDelta,
        rankChanged: previous ? previous.Rank !== member.Rank : false,
        previousRank: previous?.Rank || null,
      },
    };
  });
};

const compareRankingRows = (rankings, previousRankings) => {
  if (!previousRankings.length) {
    return rankings.map((alliance) => ({ ...alliance, comparison: null }));
  }

  const previousById = new Map(previousRankings.map((alliance) => [rowKey(alliance, 'Alliance ID'), alliance]).filter(([key]) => key));

  return rankings.map((alliance) => {
    const previous = previousById.get(rowKey(alliance, 'Alliance ID'));
    const powerDelta = previous ? toNumber(alliance.Power) - toNumber(previous.Power) : 0;

    return {
      ...alliance,
      comparison: {
        previous,
        status: previous ? (powerDelta > 0 ? 'up' : powerDelta < 0 ? 'down' : 'same') : 'new',
        powerDelta,
      },
    };
  });
};

const removedRows = (previousRows, currentRows, keyField) => {
  const currentKeys = new Set(currentRows.map((row) => rowKey(row, keyField)).filter(Boolean));
  return previousRows.filter((row) => {
    const key = rowKey(row, keyField);
    return key && !currentKeys.has(key);
  });
};

const parseJoinedDate = (value) => {
  const parts = String(value || '').split('/').map((part) => Number(part));
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) return 0;
  const [day, month, year] = parts;
  return new Date(year, month - 1, day).getTime();
};

const formatInteger = (value) => new Intl.NumberFormat('en-US').format(Math.round(value || 0));

const formatCompact = (value) => {
  const num = Number(value) || 0;
  if (num >= 1000000000) return `${(num / 1000000000).toFixed(2)}B`;
  if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return formatInteger(num);
};

const formatSignedCompact = (value) => {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${formatCompact(Math.abs(value))}`;
};

const StatCard = ({ icon: Icon, label, value, detail, color = 'from-cyan-500 to-blue-500' }) => (
  <div className="game-card p-5 min-h-[132px]">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-cyan-300 text-xs font-black uppercase tracking-widest mb-2">{label}</p>
        <p className="text-3xl md:text-4xl font-black text-white leading-none" style={{ textShadow: '0 0 16px rgba(255,255,255,0.25)' }}>
          {value}
        </p>
        {detail && <p className="text-cyan-200/70 text-sm mt-3">{detail}</p>}
      </div>
      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center border-2 border-white/25 shadow-lg`}>
        <Icon className="w-7 h-7 text-white" />
      </div>
    </div>
  </div>
);

const ViewTab = ({ active, icon: Icon, label, detail, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`w-full md:w-auto flex items-center gap-3 px-5 py-3 rounded-xl border-2 font-black transition-all ${
      active
        ? 'bg-orange-500/20 border-orange-400 text-white shadow-lg shadow-orange-500/20'
        : 'bg-[#0a1628]/70 border-cyan-500/30 text-cyan-200 hover:border-cyan-400 hover:bg-cyan-500/10'
    }`}
  >
    <Icon className={`w-5 h-5 ${active ? 'text-orange-300' : 'text-cyan-300'}`} />
    <span>{label}</span>
    {detail && <span className="text-xs font-bold opacity-70">{detail}</span>}
  </button>
);

const UpdateInfo = ({ meta }) => (
  <div className="flex-1 min-h-[52px] px-5 py-3 rounded-xl border-2 border-cyan-500/30 bg-[#0a1628]/60 flex flex-col md:flex-row md:items-center md:justify-between gap-1">
    <div className="flex items-center gap-2 text-cyan-200">
      <Clock className="w-5 h-5 text-cyan-300" />
      <span className="font-black">Ultima Actualizacion:</span>
      <span className="font-bold text-white">{formatUpdateDate(meta?.updatedAt)}</span>
    </div>
    {meta?.previousUpdatedAt && (
      <span className="text-xs text-cyan-300/70 font-bold">
        Comparando vs {formatUpdateDate(meta.previousUpdatedAt)}
      </span>
    )}
  </div>
);

const ChangeBadge = ({ comparison, showKills = false }) => {
  if (!comparison) return <span className="text-cyan-300/60 font-bold">Sin historial</span>;

  if (comparison.status === 'new') {
    return (
      <span className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-cyan-500/15 border border-cyan-300/40 text-cyan-100 font-black">
        <UserPlus className="w-4 h-4" />
        Nuevo
      </span>
    );
  }

  const isUp = comparison.powerDelta > 0;
  const isDown = comparison.powerDelta < 0;

  return (
    <div className="space-y-1">
      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg border font-black ${
        isUp
          ? 'bg-green-500/15 border-green-300/40 text-green-200'
          : isDown
            ? 'bg-red-500/15 border-red-300/40 text-red-200'
            : 'bg-slate-500/15 border-slate-300/30 text-slate-200'
      }`}>
        {isUp ? <TrendingUp className="w-4 h-4" /> : isDown ? <TrendingDown className="w-4 h-4" /> : <BarChart3 className="w-4 h-4" />}
        {isUp || isDown ? formatSignedCompact(comparison.powerDelta) : 'Sin cambio'}
      </span>
      {showKills && comparison.previous && (
        <p className={`text-xs font-bold ${comparison.killsDelta >= 0 ? 'text-pink-200' : 'text-red-200'}`}>
          Kills {formatSignedCompact(comparison.killsDelta)}
        </p>
      )}
    </div>
  );
};

const MembersView = ({
  loading,
  members,
  previousMembers = [],
  searchTerm,
  setSearchTerm,
  rankFilter,
  setRankFilter,
  careerFilter,
  setCareerFilter,
  sortBy,
  setSortBy
}) => {
  const summary = useMemo(() => {
    const totalPower = members.reduce((sum, member) => sum + toNumber(member.Power), 0);
    const totalKills = members.reduce((sum, member) => sum + toNumber(member['Army Kills']), 0);
    const topPower = members.reduce((best, member) => (toNumber(member.Power) > toNumber(best?.Power) ? member : best), null);
    const topKills = members.reduce((best, member) => (toNumber(member['Army Kills']) > toNumber(best?.['Army Kills']) ? member : best), null);
    const rankCounts = members.reduce((counts, member) => {
      const rank = member.Rank || 'Sin rango';
      counts[rank] = (counts[rank] || 0) + 1;
      return counts;
    }, {});
    const mainRank = Object.entries(rankCounts).sort((a, b) => b[1] - a[1])[0];

    return {
      totalPower,
      totalKills,
      topPower,
      topKills,
      mainRank: mainRank ? `${mainRank[0]} (${mainRank[1]})` : 'Sin datos'
    };
  }, [members]);

  const removedMembers = useMemo(() => removedRows(previousMembers, members, 'UID'), [previousMembers, members]);

  const changeSummary = useMemo(() => ({
    up: members.filter((member) => member.comparison?.status === 'up').length,
    down: members.filter((member) => member.comparison?.status === 'down').length,
    newRows: members.filter((member) => member.comparison?.status === 'new').length,
    removed: removedMembers.length
  }), [members, removedMembers]);

  const rankOptions = useMemo(() => [...new Set(members.map((member) => member.Rank).filter(Boolean))].sort(), [members]);
  const careerOptions = useMemo(() => [...new Set(members.map((member) => member.Career).filter(Boolean))].sort(), [members]);

  const filteredMembers = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    const filtered = members.filter((member) => {
      const matchesSearch = !search || [
        member.Name,
        member.UID,
        member.Career,
        member.Rank
      ].some((value) => String(value || '').toLowerCase().includes(search));
      const matchesRank = rankFilter === 'all' || member.Rank === rankFilter;
      const matchesCareer = careerFilter === 'all' || member.Career === careerFilter;
      return matchesSearch && matchesRank && matchesCareer;
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === 'power-desc') return toNumber(b.Power) - toNumber(a.Power);
      if (sortBy === 'kills-desc') return toNumber(b['Army Kills']) - toNumber(a['Army Kills']);
      if (sortBy === 'hq-desc') return toNumber(b['HQ Level']) - toNumber(a['HQ Level']);
      if (sortBy === 'joined-desc') return parseJoinedDate(b.Joined) - parseJoinedDate(a.Joined);
      if (sortBy === 'name-asc') return String(a.Name || '').localeCompare(String(b.Name || ''));
      return 0;
    });
  }, [members, searchTerm, rankFilter, careerFilter, sortBy]);

  return (
    <div className="space-y-8">
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
        <StatCard icon={Users} label="Miembros" value={loading ? '...' : members.length} detail="Total registrados" color="from-cyan-500 to-blue-500" />
        <StatCard icon={Swords} label="Poder total" value={loading ? '...' : formatCompact(summary.totalPower)} detail={summary.topPower ? `Top: ${summary.topPower.Name}` : 'Sin datos'} color="from-yellow-500 to-orange-500" />
        <StatCard icon={Skull} label="Army Kills" value={loading ? '...' : formatCompact(summary.totalKills)} detail={summary.topKills ? `Top: ${summary.topKills.Name}` : 'Sin datos'} color="from-pink-500 to-rose-500" />
        <StatCard icon={Crown} label="Rango principal" value={loading ? '...' : summary.mainRank} detail="Distribucion por rango" color="from-purple-500 to-violet-500" />
      </div>

      {previousMembers.length > 0 && (
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
          <StatCard icon={TrendingUp} label="Subieron poder" value={loading ? '...' : changeSummary.up} detail="Vs base anterior" color="from-green-500 to-emerald-500" />
          <StatCard icon={TrendingDown} label="Bajaron poder" value={loading ? '...' : changeSummary.down} detail="Vs base anterior" color="from-red-500 to-rose-500" />
          <StatCard icon={UserPlus} label="Nuevos" value={loading ? '...' : changeSummary.newRows} detail="No estaban antes" color="from-cyan-500 to-blue-500" />
          <StatCard icon={UserMinus} label="Salieron" value={loading ? '...' : changeSummary.removed} detail="Estaban en la base anterior" color="from-slate-500 to-gray-600" />
        </div>
      )}

      <div className="game-card p-5">
        <div className="grid lg:grid-cols-[1.4fr_0.8fr_0.9fr_0.9fr] gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-cyan-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar miembro, UID, profesion o rango..."
              className="w-full pl-11 pr-4 py-3 bg-[#0a1628]/80 border-2 border-cyan-500/30 rounded-xl text-white placeholder:text-cyan-500/50 focus:border-cyan-400 focus:outline-none transition-colors"
              data-testid="alliance-search-input"
            />
          </div>

          <select
            value={rankFilter}
            onChange={(event) => setRankFilter(event.target.value)}
            className="px-4 py-3 bg-[#0a1628]/80 border-2 border-cyan-500/30 rounded-xl text-white focus:border-cyan-400 focus:outline-none"
            data-testid="alliance-rank-filter"
          >
            <option value="all">Todos los rangos</option>
            {rankOptions.map((rank) => <option key={rank} value={rank}>{rank}</option>)}
          </select>

          <select
            value={careerFilter}
            onChange={(event) => setCareerFilter(event.target.value)}
            className="px-4 py-3 bg-[#0a1628]/80 border-2 border-cyan-500/30 rounded-xl text-white focus:border-cyan-400 focus:outline-none"
            data-testid="alliance-career-filter"
          >
            <option value="all">Todas las profesiones</option>
            {careerOptions.map((career) => <option key={career} value={career}>{career}</option>)}
          </select>

          <select
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
            className="px-4 py-3 bg-[#0a1628]/80 border-2 border-cyan-500/30 rounded-xl text-white focus:border-cyan-400 focus:outline-none"
            data-testid="alliance-sort-select"
          >
            <option value="power-desc">Mayor poder</option>
            <option value="kills-desc">Mas kills</option>
            <option value="hq-desc">Mayor HQ</option>
            <option value="joined-desc">Mas reciente</option>
            <option value="name-asc">Nombre A-Z</option>
          </select>
        </div>
      </div>

      <div className="game-card overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-5 border-b border-cyan-500/20">
          <div>
            <h2 className="text-2xl font-black text-white flex items-center gap-3">
              <Trophy className="w-7 h-7 text-yellow-400" />
              Miembros de la Alianza
            </h2>
            <p className="text-cyan-300/70 text-sm mt-1">
              {loading ? 'Leyendo datos...' : `${filteredMembers.length} de ${members.length} miembros visibles`}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-12 h-12 text-orange-400 animate-spin" />
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-cyan-500/40" />
            <h3 className="text-2xl font-bold text-white mb-2">Sin resultados</h3>
            <p className="text-cyan-300/70">No encontre miembros con esos filtros.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1060px]">
              <thead>
                <tr className="bg-[#0a1628]/80 border-b border-cyan-500/30">
                  <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider text-cyan-300">#</th>
                  <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider text-cyan-300">Nombre</th>
                  <th className="px-5 py-4 text-right text-xs font-black uppercase tracking-wider text-cyan-300">HQ</th>
                  <th className="px-5 py-4 text-right text-xs font-black uppercase tracking-wider text-cyan-300">Poder</th>
                  <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider text-cyan-300">Cambio</th>
                  <th className="px-5 py-4 text-right text-xs font-black uppercase tracking-wider text-cyan-300">Army Kills</th>
                  <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider text-cyan-300">Profesion</th>
                  <th className="px-5 py-4 text-right text-xs font-black uppercase tracking-wider text-cyan-300">Lv</th>
                  <th className="px-5 py-4 text-center text-xs font-black uppercase tracking-wider text-cyan-300">Rango</th>
                  <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider text-cyan-300">Unido</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((member, index) => (
                  <tr key={`${member.UID}-${member.rowId}`} className="border-b border-cyan-500/10 hover:bg-cyan-500/10 transition-colors">
                    <td className="px-5 py-4 text-cyan-300/70 font-bold">{index + 1}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500/30 to-yellow-500/30 border border-orange-400/40 flex items-center justify-center">
                          <Shield className="w-5 h-5 text-yellow-300" />
                        </div>
                        <div>
                          <p className="text-white font-black">{member.Name}</p>
                          <p className="text-cyan-300/60 text-xs">Miembro #{member.rowId}</p>
                          <p className="text-cyan-300/80 text-xs font-mono mt-1">UID: {member.UID}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right text-white font-black">Nvl. {member['HQ Level']}</td>
                    <td className="px-5 py-4 text-right text-yellow-300 font-black text-lg">{formatCompact(toNumber(member.Power))}</td>
                    <td className="px-5 py-4">
                      <ChangeBadge comparison={member.comparison} showKills />
                    </td>
                    <td className="px-5 py-4 text-right text-pink-300 font-black">{formatCompact(toNumber(member['Army Kills']))}</td>
                    <td className="px-5 py-4">
                      <div className="inline-flex items-center gap-2 text-cyan-100 font-bold">
                        <Briefcase className="w-4 h-4 text-green-300" />
                        {member.Career}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right text-green-300 font-black">{member['Career Lv']}</td>
                    <td className="px-5 py-4 text-center">
                      <span className="inline-flex items-center justify-center min-w-12 px-3 py-1 rounded-lg bg-orange-500/20 border border-orange-400/40 text-orange-200 font-black">
                        {member.Rank}
                      </span>
                      {member.comparison?.rankChanged && (
                        <p className="text-xs text-orange-200/80 mt-1">Antes {member.comparison.previousRank}</p>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="inline-flex items-center gap-2 text-cyan-100">
                        <CalendarDays className="w-4 h-4 text-cyan-300" />
                        {member.Joined}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const RankingView = ({ loading, rankings, previousRankings = [], rankingSearch, setRankingSearch }) => {
  const rankedAlliances = useMemo(() => {
    return [...rankings]
      .sort((a, b) => toNumber(b.Power) - toNumber(a.Power))
      .map((alliance, index) => ({
        ...alliance,
        computedRank: index + 1,
        powerNumber: toNumber(alliance.Power)
      }));
  }, [rankings]);

  const homeAlliance = useMemo(() => {
    return rankedAlliances.find((alliance) => alliance.Tag === HOME_ALLIANCE_TAG) || rankedAlliances[0] || null;
  }, [rankedAlliances]);

  const secondAlliance = rankedAlliances.find((alliance) => alliance.Tag !== HOME_ALLIANCE_TAG) || null;
  const mexiPower = homeAlliance?.powerNumber || 0;
  const advantageVsSecond = secondAlliance ? mexiPower - secondAlliance.powerNumber : 0;
  const topThree = rankedAlliances.slice(0, 3);
  const removedAlliances = useMemo(() => removedRows(previousRankings, rankings, 'Alliance ID'), [previousRankings, rankings]);

  const changeSummary = useMemo(() => ({
    up: rankings.filter((alliance) => alliance.comparison?.status === 'up').length,
    down: rankings.filter((alliance) => alliance.comparison?.status === 'down').length,
    newRows: rankings.filter((alliance) => alliance.comparison?.status === 'new').length,
    removed: removedAlliances.length
  }), [rankings, removedAlliances]);

  const filteredRankings = useMemo(() => {
    const search = rankingSearch.trim().toLowerCase();
    if (!search) return rankedAlliances;

    return rankedAlliances.filter((alliance) => [
      alliance.Tag,
      alliance.Name,
      alliance['Alliance ID']
    ].some((value) => String(value || '').toLowerCase().includes(search)));
  }, [rankedAlliances, rankingSearch]);

  return (
    <div className="space-y-8">
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
        <StatCard
          icon={Trophy}
          label="MEXi posicion"
          value={loading ? '...' : homeAlliance ? `#${homeAlliance.computedRank}` : 'Sin datos'}
          detail={homeAlliance ? `[${homeAlliance.Tag}] ${homeAlliance.Name}` : 'Ranking calculado por poder'}
          color="from-yellow-500 to-orange-500"
        />
        <StatCard
          icon={Swords}
          label="Poder MEXi"
          value={loading ? '...' : formatCompact(mexiPower)}
          detail="Poder total de alianza"
          color="from-cyan-500 to-blue-500"
        />
        <StatCard
          icon={BarChart3}
          label="Ventaja vs segundo"
          value={loading ? '...' : formatSignedCompact(advantageVsSecond)}
          detail={secondAlliance ? `Vs [${secondAlliance.Tag}] ${secondAlliance.Name}` : 'Sin rival cercano'}
          color="from-green-500 to-emerald-500"
        />
        <StatCard
          icon={Medal}
          label="Alianzas"
          value={loading ? '...' : rankedAlliances.length}
          detail="Servidor 250"
          color="from-purple-500 to-violet-500"
        />
      </div>

      {previousRankings.length > 0 && (
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
          <StatCard icon={TrendingUp} label="Subieron poder" value={loading ? '...' : changeSummary.up} detail="Vs base anterior" color="from-green-500 to-emerald-500" />
          <StatCard icon={TrendingDown} label="Bajaron poder" value={loading ? '...' : changeSummary.down} detail="Vs base anterior" color="from-red-500 to-rose-500" />
          <StatCard icon={UserPlus} label="Nuevas" value={loading ? '...' : changeSummary.newRows} detail="No estaban antes" color="from-cyan-500 to-blue-500" />
          <StatCard icon={UserMinus} label="Salieron" value={loading ? '...' : changeSummary.removed} detail="Estaban en la base anterior" color="from-slate-500 to-gray-600" />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        {topThree.map((alliance, index) => {
          const podiumStyles = [
            'border-yellow-300 shadow-yellow-500/30',
            'border-cyan-300 shadow-cyan-500/25',
            'border-orange-300 shadow-orange-500/25'
          ];
          const badgeStyles = [
            'from-yellow-400 to-orange-500',
            'from-cyan-400 to-blue-500',
            'from-orange-400 to-amber-600'
          ];

          return (
            <div key={alliance['Alliance ID'] || alliance.Tag} className={`game-card p-5 border-2 ${podiumStyles[index] || 'border-cyan-400'}`}>
              <div className="flex items-center justify-between gap-4 mb-5">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${badgeStyles[index]} flex items-center justify-center border-2 border-white/30 shadow-lg`}>
                  <span className="text-white text-2xl font-black">#{alliance.computedRank}</span>
                </div>
                <div className="text-right">
                  <p className="text-cyan-300 text-xs font-black uppercase tracking-widest">Poder</p>
                  <p className="text-3xl font-black text-yellow-300">{formatCompact(alliance.powerNumber)}</p>
                </div>
              </div>
              <p className="text-orange-300 text-sm font-black tracking-widest">[{alliance.Tag}]</p>
              <h3 className="text-2xl font-black text-white truncate">{alliance.Name}</h3>
              <p className="text-cyan-300/70 text-sm mt-3 font-mono truncate">{alliance['Alliance ID']}</p>
            </div>
          );
        })}
      </div>

      <div className="game-card p-5">
        <div className="grid lg:grid-cols-[1fr_auto] gap-3 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-cyan-400" />
            <input
              type="text"
              value={rankingSearch}
              onChange={(event) => setRankingSearch(event.target.value)}
              placeholder="Buscar alianza por tag, nombre o ID..."
              className="w-full pl-11 pr-4 py-3 bg-[#0a1628]/80 border-2 border-cyan-500/30 rounded-xl text-white placeholder:text-cyan-500/50 focus:border-cyan-400 focus:outline-none transition-colors"
              data-testid="ranking-search-input"
            />
          </div>
          <div className="text-cyan-300/80 font-bold px-2">
            {loading ? 'Leyendo ranking...' : `${filteredRankings.length} de ${rankedAlliances.length} alianzas visibles`}
          </div>
        </div>
      </div>

      <div className="game-card overflow-hidden">
        <div className="p-5 border-b border-cyan-500/20">
          <h2 className="text-2xl font-black text-white flex items-center gap-3">
            <Trophy className="w-7 h-7 text-yellow-400" />
            Alianzas del Servidor 250
          </h2>
          <p className="text-cyan-300/70 text-sm mt-1">
            Ranking calculado por poder total de alianza.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-12 h-12 text-orange-400 animate-spin" />
          </div>
        ) : filteredRankings.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-cyan-500/40" />
            <h3 className="text-2xl font-bold text-white mb-2">Sin resultados</h3>
            <p className="text-cyan-300/70">No encontre alianzas con esa busqueda.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1180px]">
              <thead>
                <tr className="bg-[#0a1628]/80 border-b border-cyan-500/30">
                  <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider text-cyan-300">Posicion</th>
                  <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider text-cyan-300">Tag</th>
                  <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider text-cyan-300">Nombre</th>
                  <th className="px-5 py-4 text-right text-xs font-black uppercase tracking-wider text-cyan-300">Poder</th>
                  <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider text-cyan-300">Cambio</th>
                  <th className="px-5 py-4 text-right text-xs font-black uppercase tracking-wider text-cyan-300">Diferencia vs MEXi</th>
                  <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider text-cyan-300">Alliance ID</th>
                </tr>
              </thead>
              <tbody>
                {filteredRankings.map((alliance) => {
                  const diff = alliance.powerNumber - mexiPower;
                  const isHome = alliance.Tag === HOME_ALLIANCE_TAG;

                  return (
                    <tr key={`${alliance['Alliance ID']}-${alliance.rowId}`} className={`border-b border-cyan-500/10 transition-colors ${isHome ? 'bg-yellow-500/10 hover:bg-yellow-500/15' : 'hover:bg-cyan-500/10'}`}>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center justify-center min-w-12 px-3 py-1 rounded-lg border font-black ${
                          alliance.computedRank === 1
                            ? 'bg-yellow-500/20 border-yellow-300 text-yellow-200'
                            : 'bg-cyan-500/10 border-cyan-400/40 text-cyan-200'
                        }`}>
                          #{alliance.computedRank}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-orange-300 font-black tracking-widest">[{alliance.Tag}]</span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl border flex items-center justify-center ${isHome ? 'bg-yellow-500/20 border-yellow-300/50' : 'bg-cyan-500/10 border-cyan-400/40'}`}>
                            <Shield className={`w-5 h-5 ${isHome ? 'text-yellow-300' : 'text-cyan-300'}`} />
                          </div>
                          <div>
                            <p className="text-white font-black">{alliance.Name}</p>
                            {isHome && <p className="text-yellow-300/80 text-xs font-bold">Tu alianza</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right text-yellow-300 font-black text-lg">{formatCompact(alliance.powerNumber)}</td>
                      <td className="px-5 py-4">
                        <ChangeBadge comparison={alliance.comparison} />
                      </td>
                      <td className={`px-5 py-4 text-right font-black ${diff >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                        {formatSignedCompact(diff)}
                      </td>
                      <td className="px-5 py-4">
                        <div className="inline-flex items-center gap-2 text-cyan-300/80 font-mono text-sm">
                          <IdCard className="w-4 h-4 text-cyan-400" />
                          {alliance['Alliance ID']}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const AlliancePage = () => {
  const [activeView, setActiveView] = useState('members');
  const [members, setMembers] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [previousMembers, setPreviousMembers] = useState([]);
  const [previousRankings, setPreviousRankings] = useState([]);
  const [csvMeta, setCsvMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [rankFilter, setRankFilter] = useState('all');
  const [careerFilter, setCareerFilter] = useState('all');
  const [sortBy, setSortBy] = useState('power-desc');
  const [rankingSearch, setRankingSearch] = useState('');

  const loadAllianceData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [memberRows, rankingRows, previousMemberRows, previousRankingRows, meta] = await Promise.all([
        loadCsvFile(MEMBERS_CSV_PATH),
        loadCsvFile(RANKINGS_CSV_PATH),
        loadOptionalCsvFile(PREVIOUS_MEMBERS_CSV_PATH),
        loadOptionalCsvFile(PREVIOUS_RANKINGS_CSV_PATH),
        fetch(`${CSV_META_PATH}?v=${Date.now()}`, { cache: 'no-store' }).then((response) => (response.ok ? response.json() : {}))
      ]);
      setPreviousMembers(previousMemberRows);
      setPreviousRankings(previousRankingRows);
      setCsvMeta(meta || {});
      setMembers(compareMemberRows(memberRows, previousMemberRows));
      setRankings(compareRankingRows(rankingRows, previousRankingRows));
    } catch (err) {
      console.error('Error loading alliance data:', err);
      setError('No pude cargar los datos de la alianza.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllianceData();
  }, []);

  return (
    <div className="space-y-8">
      <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-5">
        <div>
          <h1 className="text-5xl font-black text-white mb-3" style={{ textShadow: '0 0 20px rgba(255,255,255,0.2)' }}>
            Alianza <span className="text-cyan-400">MEXi</span>
          </h1>
          <p className="text-cyan-300/75">
            {loading ? 'Cargando datos...' : `${members.length} miembros y ${rankings.length} alianzas cargadas`}
          </p>
        </div>

        <button
          onClick={loadAllianceData}
          className="game-btn game-btn-blue flex items-center justify-center gap-3 text-base px-6 py-3"
          data-testid="refresh-alliance-btn"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {error && (
        <div className="game-card border-red-500 p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-400" />
            <span className="text-cyan-100">{error}</span>
          </div>
        </div>
      )}

      <div className="game-card p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <ViewTab
            active={activeView === 'members'}
            icon={Users}
            label="Miembros MEXi"
            detail={loading ? '...' : `${members.length}`}
            onClick={() => setActiveView('members')}
            testId="alliance-members-tab"
          />
          <ViewTab
            active={activeView === 'ranking'}
            icon={BarChart3}
            label="Ranking Servidor 250"
            detail={loading ? '...' : `${rankings.length}`}
            onClick={() => setActiveView('ranking')}
            testId="alliance-ranking-tab"
          />
          <UpdateInfo meta={activeView === 'members' ? csvMeta.members : csvMeta.ranking} />
        </div>
      </div>

      {activeView === 'members' ? (
        <MembersView
          loading={loading}
          members={members}
          previousMembers={previousMembers}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          rankFilter={rankFilter}
          setRankFilter={setRankFilter}
          careerFilter={careerFilter}
          setCareerFilter={setCareerFilter}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
      ) : (
        <RankingView
          loading={loading}
          rankings={rankings}
          previousRankings={previousRankings}
          rankingSearch={rankingSearch}
          setRankingSearch={setRankingSearch}
        />
      )}
    </div>
  );
};

export default AlliancePage;
